import { useEffect } from "react";
import { useSEOHead } from "@/hooks/useSEOHead";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import type { ArticleData } from "@/mocks/articles";

interface ArticleLayoutProps {
  article: ArticleData;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const baseUrl = (() => {
    try {
      const env = (import.meta as Record<string, { VITE_SITE_URL?: string }>).env;
      return (env.VITE_SITE_URL || "https://mulebuyspreadsheets.hair").replace(/\/+$/, "");
    } catch {
      return "https://mulebuyspreadsheets.hair";
    }
  })();

  const articlePath = `/${article.slug}`;

  useSEOHead({
    title: `${article.title} | MulebuySpreadsheets`,
    description: article.subtitle,
    keywords: `Mulebuy spreadsheet, ${article.title.toLowerCase()}, spreadsheet shopping, mulebuy spreadsheet guide, ${article.slug.replace(/-/g, " ")}`,
    canonicalPath: articlePath,
    ogImage: article.heroImage,
    ogType: "article",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.subtitle,
        image: article.heroImage,
        author: { "@type": "Organization", name: "MulebuySpreadsheets", url: baseUrl },
        publisher: { "@type": "Organization", name: "MulebuySpreadsheets", url: baseUrl },
        datePublished: "2026-06-01",
        dateModified: "2026-06-22",
        mainEntityOfPage: { "@type": "WebPage", "@id": `${baseUrl}${articlePath}` },
      },
    ],
  });

  return (
    <main>
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full h-[420px] md:h-[520px] flex items-center overflow-hidden">
        <img
          src={article.heroImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/45"></div>

        <div className="relative z-10 w-full px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-background-50/80 hover:text-background-50 mb-6 transition-colors duration-200 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight max-w-3xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm md:text-base text-background-50/70 max-w-xl leading-relaxed">
            {article.subtitle}
          </p>
        </div>
      </section>

      {/* Overview Stats */}
      <section className="py-14 md:py-18 px-6 md:px-10 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-foreground-500 font-label mb-3">Article Overview</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight">
            {article.introTitle}
          </h2>
          <p className="mt-3 text-sm text-foreground-600 max-w-lg">
            This article covers essential information about the Mulebuy spreadsheet system to help you become a more informed shopper.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-accent-100/60 rounded-lg p-8 md:p-10">
              <span className="font-heading text-7xl md:text-8xl font-bold text-accent-600">{article.stat1Value}</span>
              <p className="mt-3 text-foreground-700 text-sm leading-relaxed max-w-xs">
                {article.stat1Label}
              </p>
            </div>
            <div className="bg-secondary-100/60 rounded-lg p-8 md:p-10">
              <span className="font-heading text-7xl md:text-8xl font-bold text-secondary-600">{article.stat2Value}</span>
              <p className="mt-3 text-foreground-700 text-sm leading-relaxed max-w-xs">
                {article.stat2Label}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-14 md:py-18 px-6 md:px-10 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <article>
            {article.sections.map((section, sIdx) => (
              <div key={sIdx} className={sIdx > 0 ? "mt-14" : ""}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  {section.heading}
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  {section.paragraphs.map((p, pIdx) => (
                    <p key={pIdx} dangerouslySetInnerHTML={{ __html: p }}></p>
                  ))}
                </div>
              </div>
            ))}
          </article>

          {/* Internal Navigation */}
          <div className="mt-14 pt-10 border-t border-background-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div>
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line"></i>
                {article.backLabel}
              </Link>
              <p className="mt-3 text-xs text-foreground-500">
                Return to the homepage for full category access and more Mulebuy spreadsheet resources.
              </p>
            </div>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-foreground-950 text-background-50 rounded-full text-sm md:text-base font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              mulebuy spreadsheet
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}